<script setup lang="ts">
import DeleteEntityDialog from '@/common/components/DeleteEntityDialog.vue';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { Button, Card, Column, DataTable, InputText, Menu, type DataTableRowDoubleClickEvent } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { usePackagingAuditApi } from '@/audit/api/packagingAuditApi';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import { useRefreshPackagingStore } from '../commands/refreshPackagingStore';
import EditPackagingDialog from './EditPackagingDialog.vue';
import { useDeletePackaging } from '../api/deletePackaging';

const selectedColumnsStorageKey = 'packaging-table-columns';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.packaging);

const packagingStore = usePackagingStore();
const refreshPackagingStore = useRefreshPackagingStore();
const deletePackaging = useDeletePackaging();
const packagingAuditApi = usePackagingAuditApi();
const actionFailedAlert = useActionFailedAlert();

const selectedRow = ref<{ id: string } | null>(null);
const menuPackagingId = ref<string>();
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const packagingMenu = useTemplateRef('packaging-menu');
const auditDialog = useTemplateRef('audit-dialog');

const columnDefinitions = computed<ColumnField[]>(() => [
  { field: 'name', header: translations.value.generic.name, filterType: 'text' },
  { field: 'level', header: translations.value.generic.type, filterType: 'text' },
  { field: 'description', header: translations.value.generic.description, filterType: 'text' },
]);

const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector({ storageKey: selectedColumnsStorageKey, columns: columnDefinitions });

const rows = computed(() => {
  return packagingStore.sortedByName.map((item) => ({
    id: item.id,
    name: item.name,
    level: item.level,
    description: item.description,
  }));
});

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => rows.value.length,
});

// Clear corrupt persisted sort state before the DataTable restores it while rendering.
clearCorruptDataTableState('packaging-table-state');

onMounted(() => {
  refreshPackagingStore.initialize();
});

const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

function onAdd() {
  editDialog.value?.show();
}

function onRowDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const packaging = ev.data as { id: string };
  selectedRow.value = { id: packaging.id };
  editDialog.value?.show(packaging.id);
}

const packagingMenuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuPackagingId.value;
      if (!id) return;
      editDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.copy,
    icon: 'pi pi-copy',
    command() {
      const id = menuPackagingId.value;
      if (!id) return;
      editDialog.value?.showCopy(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuPackagingId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuPackagingId.value;
      if (!id) return;

      const response = await packagingAuditApi.execute({ id });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({ audit: response.audit });
    },
  },
]);

function togglePackagingMenu(ev: Event, id: string) {
  menuPackagingId.value = id;
  packagingMenu.value?.toggle(ev);
}

function onSaved(id: string) {
  selectedRow.value = { id };
}

function onDeleted(id: string) {
  if (selectedRow.value?.id === id) {
    selectedRow.value = null;
  }
}

function getPackagingForDelete(id: string) {
  return Promise.resolve({
    id,
    name: packagingStore.getById(id)?.name ?? id,
  });
}
</script>

<template>
  <Card>
    <template #title>
      <div class="title-row">
        <span>{{ pageTranslations.packaging }}</span>
        <div class="actions">
          <span v-if="hasActiveFilters && filteredCount < rows.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ rows.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
          <Button :label="translations.generic.add" @click="onAdd"></Button>
          <InputText v-model="filters.global.value" :placeholder="translations.generic.search"></InputText>
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:selection="selectedRow"
        v-model:filters="filters"
        v-model:multiSortMeta="multiSortMeta"
        :class="sortBadgeClass"
        :value="rows"
        data-key="id"
        selectionMode="single"
        sort-mode="multiple"
        stateStorage="local"
        stateKey="packaging-table-state"
        filterDisplay="menu"
        @filter="onFilter"
        @row-dblclick="onRowDoubleClick"
      >
        <Column v-for="(col, index) of selectedColumns" :key="col.field + '_' + index" :field="col.field" :header="col.header" sortable
          :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
          <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
            <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
              :options="col.filterOptions"
              :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
          </template>
        </Column>
        <Column column-key="column-selector" class="w-12 !text-end">
          <template #header>
            <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
          </template>
          <template #body="{ data }">
            <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
              @click="(ev) => togglePackagingMenu(ev, data.id)"></Button>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <EditPackagingDialog ref="edit-dialog" @saved="onSaved"></EditPackagingDialog>
  <DeleteEntityDialog
    ref="delete-dialog"
    :delete="(id) => deletePackaging.execute({ id })"
    :getById="getPackagingForDelete"
    :translate="(t) => t.packaging.deleteDialog"
    @deleted="onDeleted"
  ></DeleteEntityDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="packaging-menu" :model="packagingMenuItems" :popup="true" />
</template>

<style scoped>
.title-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}
</style>
